class NasaemSlider {
  constructor(root) {
    this.root = root;
    this.track = root.querySelector('[data-nasaem-track]');
    this.dotsHost = root.querySelector('[data-nasaem-dots]');
    this.dots = [];
    this.isReviews = root.classList.contains('nasaem-reviews');
    if (!this.track) return;
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.track.addEventListener('scroll', () => this.onScroll(), { passive: true });
    this.isInteractiveTarget = (event) =>
      Boolean(event.target.closest('a, button, input, label, select, textarea, product-form'));
    this.track.addEventListener(
      'pointerdown',
      (event) => {
        this.ignoreDrag = this.isInteractiveTarget(event);
      },
      { passive: true }
    );
    this.track.addEventListener(
      'pointerup',
      (event) => {
        if (this.ignoreDrag || this.isInteractiveTarget(event)) {
          this.ignoreDrag = false;
        }
      },
      { passive: true }
    );
    this.buildDots();
    this._onResize = () => {
      this.buildDots();
      if (this.isReviews) this.fitVisibleHeight();
    };
    window.addEventListener('resize', this._onResize, { passive: true });
    if (typeof ResizeObserver === 'function') {
      let lastWidth = this.track.clientWidth;
      this._ro = new ResizeObserver(() => {
        if (this.track.clientWidth === lastWidth) return;
        lastWidth = this.track.clientWidth;
        this._onResize();
      });
      this._ro.observe(this.track);
    }
    if (this.isReviews) {
      this.track.addEventListener('load', this._onResize, true);
      document.fonts?.ready.then(() => {
        if (this.root.isConnected) this.fitVisibleHeight();
      });
    }
    this.sync();
    if (this.isReviews) this.fitVisibleHeight();
    const ms = Number(this.root.dataset.autoplayMs || 0);
    if (ms > 0 && !this.reduced.matches) {
      this.autoplayMs = ms;
      this.startAutoplay();
      this.root.addEventListener('pointerenter', () => this.stopAutoplay());
      this.root.addEventListener('pointerleave', () => this.startAutoplay());
      this.track.addEventListener('pointerdown', () => this.stopAutoplay(), { passive: true });
      this.root.addEventListener('focusin', () => this.stopAutoplay());
      this.root.addEventListener('focusout', (event) => {
        if (!this.root.contains(event.relatedTarget)) this.startAutoplay();
      });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.stopAutoplay();
        else this.startAutoplay();
      });
    }
  }

  get cards() {
    return [...this.track.children];
  }

  /* How many cards fit in one viewport of the track (≥1). */
  perView() {
    const cards = this.cards;
    if (cards.length < 2) return 1;
    const step = Math.abs(cards[1].offsetLeft - cards[0].offsetLeft) || cards[0].offsetWidth || 1;
    return Math.max(1, Math.round(this.track.clientWidth / step));
  }

  pageCount() {
    return Math.max(1, Math.ceil(this.cards.length / this.perView()));
  }

  /* Dots = pages (not cards). Re-built on resize so mobile/desktop counts differ. */
  buildDots() {
    if (!this.dotsHost) return;
    const pages = this.pageCount();
    if (this._builtPages === pages) return;
    this._builtPages = pages;
    const label = this.dotsHost.dataset.dotLabel || '';
    const maxDots = parseInt(this.dotsHost.dataset.dotsMax || '8', 10) || 8;
    this.dotsHost.replaceChildren();
    this.dots = [];
    this.counter = null;
    this.dotsHost.classList.toggle('nasaem-dots--counter', pages > maxDots);
    if (pages > maxDots) {
      // Too many pages for dots (e.g. 26 reviews on mobile): compact "n / total" pager.
      const make = (dir) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'nasaem-dots__arrow';
        btn.setAttribute('aria-label', dir < 0 ? (this.dotsHost.dataset.prevLabel || 'السابق') : (this.dotsHost.dataset.nextLabel || 'التالي'));
        btn.innerHTML = dir < 0
          ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
          : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        btn.addEventListener('click', () => {
          const total = this.pageCount();
          this.goToPage((this.currentPage() + dir + total) % total);
        });
        return btn;
      };
      const counter = document.createElement('span');
      counter.className = 'nasaem-dots__counter';
      counter.setAttribute('aria-live', 'polite');
      this.counter = counter;
      this.dotsHost.append(make(-1), counter, make(1));
    } else {
      for (let i = 0; i < pages; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', `${label} ${i + 1}`.trim());
        dot.addEventListener('click', () => this.goToPage(i));
        this.dotsHost.append(dot);
        this.dots.push(dot);
      }
    }
    this.dotsHost.hidden = pages < 2;
    this.sync();
  }

  goToPage(page) {
    const card = this.cards[Math.min(this.cards.length - 1, page * this.perView())];
    if (card) this.scrollToCard(card);
  }

  currentPage() {
    const cards = this.cards;
    const trackRect = this.track.getBoundingClientRect();
    const rtl = getComputedStyle(this.track).direction === 'rtl';
    let active = 0;
    let nearest = Infinity;
    cards.forEach((card, index) => {
      const rect = card.getBoundingClientRect();
      const distance = Math.abs(rtl ? rect.right - trackRect.right : rect.left - trackRect.left);
      if (distance < nearest) {
        nearest = distance;
        active = index;
      }
    });
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    if (maxScroll > 1 && Math.abs(this.track.scrollLeft) >= maxScroll - 2) return this.pageCount() - 1;
    return Math.min(this.pageCount() - 1, Math.floor(active / this.perView()));
  }

  startAutoplay() {
    this.stopAutoplay();
    if (!this.autoplayMs) return;
    this._timer = setInterval(() => this.next(), this.autoplayMs);
  }

  stopAutoplay() {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }

  next() {
    if (!this.root.isConnected) return this.destroy();
    if (document.hidden || this.reduced.matches) return;
    if (this.root.querySelector('[data-nasaem-review-video].is-playing')) return;
    const pages = this.pageCount();
    if (pages < 2) return;
    this.goToPage((this.currentPage() + 1) % pages);
  }

  scrollToCard(card) {
    const trackRect = this.track.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const rtl = getComputedStyle(this.track).direction === 'rtl';
    // Move only this horizontal track. scrollIntoView also moves page ancestors.
    this.track.scrollBy({
      left: rtl ? cardRect.right - trackRect.right : cardRect.left - trackRect.left,
      behavior: this.reduced.matches ? 'auto' : 'smooth',
    });
  }

  destroy() {
    this.stopAutoplay();
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    this._ro?.disconnect();
    if (this.track && this._onResize) this.track.removeEventListener('load', this._onResize, true);
  }

  onScroll() {
    this.sync();
    if (this.isReviews) {
      clearTimeout(this._fitTimer);
      this._fitTimer = setTimeout(() => this.fitVisibleHeight(), 120);
    }
  }

  sync() {
    if (this.counter) {
      // The counter is direction:rtl, so the bidi algorithm lays "1 / 13" out as 13 / 1
      // (current page on the right, total on the left) — keep the logical order here.
      const cur = this.currentPage() + 1;
      const total = this.pageCount();
      this.counter.textContent = `${cur} / ${total}`;
      return;
    }
    if (!this.dots.length) return;
    const active = this.currentPage();
    this.dots.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === active);
      if (index === active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  /* Reviews: the row is only as tall as the cards currently in view, so a text-only
     review is not padded to the height of a video card that sits off-screen. */
  fitVisibleHeight() {
    if (!this.isReviews) return;
    const track = this.track;
    const trackRect = track.getBoundingClientRect();
    let max = 0;
    let visibleMax = 0;
    this.cards.forEach((card) => {
      card.style.height = 'auto';
      const r = card.getBoundingClientRect();
      max = Math.max(max, r.height);
      const visible = Math.min(r.right, trackRect.right) - Math.max(r.left, trackRect.left) > r.width * 0.35;
      if (visible) visibleMax = Math.max(visibleMax, r.height);
    });
    const target = Math.ceil(visibleMax || max);
    if (target > 0 && track.style.height !== target + 'px') track.style.height = target + 'px';
  }
}

class NasaemBundles {
  constructor(root) {
    this.root = root;
    const form = root.closest('.nasaem-pdp')?.querySelector('form[data-type="add-to-cart-form"], product-form form');
    this.qty = form?.querySelector('[data-nasaem-qty]') || document.querySelector('[data-nasaem-qty]');
    this.options = [...root.querySelectorAll('[data-nasaem-bundle]')];
    this.options.forEach((option) => {
      option.addEventListener('click', () => this.select(option));
      const input = option.querySelector('input');
      if (input) input.addEventListener('change', () => this.select(option));
    });
    const checked = this.options.find((option) => option.querySelector('input')?.checked);
    if (checked) this.apply(checked);
  }

  select(option) {
    this.options.forEach((item) => item.classList.toggle('is-selected', item === option));
    const input = option.querySelector('input');
    if (input) input.checked = true;
    this.apply(option);
  }

  apply(option) {
    const quantity = Number(option.dataset.qty || 1);
    if (this.qty) {
      this.qty.value = String(quantity);
      this.qty.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const code = (option.dataset.discountCode || '').trim();
    if (code && code !== this.appliedCode) {
      this.appliedCode = code;
      // Shopify's /discount/CODE route stores the code for checkout (same-origin cookie).
      fetch(`${window.Shopify?.routes?.root || '/'}discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' }).catch(() => {});
    }
    this.root.dispatchEvent(new CustomEvent('nasaem:bundle', { bubbles: true, detail: { quantity, code } }));
  }
}

function initNasaemSliders(root) {
  (root || document).querySelectorAll('[data-nasaem-slider]').forEach((el) => {
    if (el.dataset.sliderBound === 'true') return;
    el.dataset.sliderBound = 'true';
    el.nasaemSlider = new NasaemSlider(el);
  });
}

function initNasaemProduct(root) {
  (root || document).querySelectorAll('[data-nasaem-bundles]').forEach((el) => {
    if (el.dataset.bundlesBound === 'true') return;
    el.dataset.bundlesBound = 'true';
    new NasaemBundles(el);
  });
  (root || document).querySelectorAll('[data-nasaem-thumb]').forEach((thumb) => {
    if (thumb.dataset.thumbBound === 'true') return;
    thumb.dataset.thumbBound = 'true';
    thumb.addEventListener('click', () => {
      const stage = thumb.closest('.nasaem-pdp')?.querySelector('[data-nasaem-stage]') || document.querySelector('[data-nasaem-stage]');
      const src = thumb.dataset.src;
      if (stage && src) {
        const img = stage.querySelector('img');
        if (img) img.src = src;
      }
      thumb.parentElement?.querySelectorAll('[data-nasaem-thumb]').forEach((other) => other.setAttribute('aria-pressed', String(other === thumb)));
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initNasaemSliders(document);
  initNasaemProduct(document);
});

document.addEventListener('shopify:section:load', (event) => {
  initNasaemSliders(event.target);
  initNasaemProduct(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-nasaem-slider]').forEach((el) => el.nasaemSlider?.destroy());
});

document.addEventListener('click', (event) => {
  const play = event.target.closest('[data-nasaem-review-play]');
  if (!play) return;
  event.preventDefault();
  const media = play.closest('[data-nasaem-review-video]');
  if (!media) return;

  document.querySelectorAll('[data-nasaem-review-video]').forEach((other) => {
    if (other === media) return;
    const otherVideo = other.querySelector('video');
    if (otherVideo) {
      otherVideo.pause();
      otherVideo.controls = false;
    }
    const otherFrame = other.querySelector('iframe');
    if (otherFrame) {
      otherFrame.remove();
    }
    other.classList.remove('is-playing');
  });

  const video = media.querySelector('video');
  if (video) {
    media.classList.add('is-playing');
    video.controls = true;
    video.play().catch(() => {});
    if (!video.dataset.endedBound) {
      video.dataset.endedBound = 'true';
      video.addEventListener('ended', () => {
        media.classList.remove('is-playing');
        video.controls = false;
      });
    }
    return;
  }

  const type = media.dataset.videoType;
  const id = media.dataset.videoId;
  if (!type || !id) return;
  let src = '';
  if (type === 'youtube') {
    src = 'https://www.youtube.com/embed/' + id + '?autoplay=1&rel=0&modestbranding=1&playsinline=1';
  } else if (type === 'vimeo') {
    src = 'https://player.vimeo.com/video/' + id + '?autoplay=1';
  } else {
    return;
  }
  const iframe = document.createElement('iframe');
  iframe.className = 'nasaem-review-card__video';
  iframe.src = src;
  iframe.setAttribute('allow', 'autoplay; encrypted-media; fullscreen');
  iframe.allowFullscreen = true;
  iframe.title = play.getAttribute('aria-label') || 'فيديو';
  media.querySelectorAll('img').forEach((img) => img.remove());
  media.classList.add('is-playing');
  media.appendChild(iframe);
});

document.addEventListener('click', (event) => {
  const play = event.target.closest('[data-nasaem-video-play]');
  if (!play) return;
  event.preventDefault();
  const root = play.closest('[data-nasaem-video]');
  if (!root || root.classList.contains('is-playing')) return;

  const type = root.dataset.videoType;
  const media = root.querySelector('.nasaem-video__media');
  if (!media) return;

  const startHosted = (video) => {
    root.classList.add('is-playing');
    video.controls = true;
    video.muted = false;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') {
      attempt.catch(() => {
        video.controls = true;
      });
    }
  };

  const video = media.querySelector('video');
  if (video) {
    startHosted(video);
    return;
  }

  const id = root.dataset.videoId;
  if (!id || (type !== 'youtube' && type !== 'vimeo')) return;

  let src = '';
  if (type === 'youtube') {
    src =
      'https://www.youtube.com/embed/' +
      id +
      '?autoplay=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1';
  } else {
    src = 'https://player.vimeo.com/video/' + id + '?autoplay=1';
  }

  const iframe = document.createElement('iframe');
  iframe.className = 'nasaem-video__file';
  iframe.src = src;
  iframe.setAttribute('allow', 'autoplay; encrypted-media; fullscreen');
  iframe.allowFullscreen = true;
  iframe.title = play.getAttribute('aria-label') || 'فيديو';
  media.appendChild(iframe);
  root.classList.add('is-playing');
});
